import React from "react";
import { NodeProps, useReactFlow } from "reactflow";
import { operationType } from "../../../../../services/types";
import { SettingsBlock, settingsDataType } from "../../../../settingsBlock";
import { IoCheckmarkCircleOutline, IoWarningOutline } from "react-icons/io5";
import { ExpandedBlock } from "../../../expanded";
import { updateNodeValue } from "../../../../../services/method";
import { TextInput } from "../../../../form/textInput";
import { Switch } from "../../../../form/switch";
import { Loader, Main, Styled } from "./style";
import { webURL } from "../../../../../services/API";
import axios from "axios";
import { AuthenticateSelect } from "../../../../auth/select";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSync } from "@fortawesome/free-solid-svg-icons";
import { useSearchParams } from "react-router-dom";

// const webURL = "https://armorplated-alysha-nonchronically.ngrok-free.dev";

type OutlookFolder = {
  id: string;
  displayName: string;
  parentFolderId?: string;
  childFolderCount?: number;
  totalItemCount?: number;
  unreadItemCount?: number;
};

type OutlookNewEmailTriggerProps = NodeProps<operationType> & {
  setChildMinimize?: (fn: (id: string) => void) => void;
  setChildSettings?: (fn: (id: string) => void) => void;
};

function generateClientState() {
  return `outlook_${Math.random().toString(36).slice(2, 12)}_${Date.now()}`;
}

function normalizeTriggerError(error: any) {
  const payload = error?.response?.data ?? error?.message ?? "Unknown error";
  const text = typeof payload === "string" ? payload : JSON.stringify(payload);

  if (
    /Mail\.Read/i.test(text) ||
    /admin consent/i.test(text) ||
    /consent/i.test(text)
  ) {
    return "Admin consent required (Mail.Read Application)";
  }
  if (/AccessDenied/i.test(text)) {
    return `AccessDenied: ${text}`;
  }
  return text;
}

export function OutlookTrigger({
  id,
  data,
  setChildMinimize,
  setChildSettings,
}: OutlookNewEmailTriggerProps) {
  const MAX_SUBSCRIPTION_MINUTES = 10070; // Graph limit
  const { setNodes } = useReactFlow();
  const [settings, setSettings] = React.useState<settingsDataType>({
    isToggle: false,
  });
  const [isExpanded, setIsExpanded] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(false);
  const [statusText, setStatusText] = React.useState<string>("");
  const [searchParams] = useSearchParams();
  const tokenRef = React.useRef<string>("");
  const projectId = searchParams.get("id");
  const [folders, setFolders] = React.useState<OutlookFolder[]>([]);
  const [isFoldersLoading, setIsFoldersLoading] = React.useState(false);
  const [folderError, setFolderError] = React.useState("");
  const hasAnyFilters = React.useMemo(
    () =>
      !!data.value?.filterQuery ||
      !!data.value?.hasAttachmentsFilter ||
      (data.value?.readStatus && data.value.readStatus !== "all") ||
      !!data.value?.sender ||
      (data.value?.foldersInclude || []).length > 0 ||
      (data.value?.foldersExclude || []).length > 0,
    [
      data.value?.filterQuery,
      data.value?.hasAttachmentsFilter,
      data.value?.readStatus,
      data.value?.sender,
      data.value?.foldersInclude,
      data.value?.foldersExclude,
    ]
  );
  const [showFilters, setShowFilters] = React.useState<boolean>(hasAnyFilters);
  const filterSummary = React.useMemo(() => {
    const summary: string[] = [];
    if (data.value?.filterQuery?.trim()) summary.push("Custom OData");
    if (data.value?.sender?.trim()) summary.push(`Sender: ${data.value.sender.trim()}`);
    if (data.value?.hasAttachmentsFilter) summary.push("Has attachments");
    if (data.value?.readStatus && data.value.readStatus !== "all") {
      summary.push(`Read: ${data.value.readStatus}`);
    }
    const includeCount = (data.value?.foldersInclude || []).length;
    const excludeCount = (data.value?.foldersExclude || []).length;
    if (includeCount) summary.push(`Include folders: ${includeCount}`);
    if (excludeCount) summary.push(`Exclude folders: ${excludeCount}`);

    return summary.length ? summary.join(" • ") : "No filters configured";
  }, [
    data.value?.filterQuery,
    data.value?.sender,
    data.value?.hasAttachmentsFilter,
    data.value?.readStatus,
    data.value?.foldersInclude,
    data.value?.foldersExclude,
  ]);

  const latestValueRef = React.useRef<any>(data.value);
  React.useEffect(() => {
    latestValueRef.current = data.value;
  }, [data.value]);

  React.useEffect(() => {
    const bearer =
      data.value?.connection?.access_token ||
      data.value?.connection?.accessToken ||
      "";
    if (bearer) {
      tokenRef.current = bearer;
    }
  }, [data.value?.connection]);

  React.useEffect(() => {
    if (setChildMinimize) {
      setChildMinimize(() => {
        setIsExpanded((prev) => !prev);
      });
    }
    if (setChildSettings) {
      setChildSettings(() => {
        setSettings((prev) => ({ ...prev, isToggle: !prev.isToggle }));
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setChildMinimize, setChildSettings]);

  React.useEffect(() => {
    if (!data.value) {
      updateNodeValue(id, setNodes, {
        folder: "Inbox",
        changeType: "created,updated",
        includeAttachments: false,
        hasAttachmentsFilter: false,
        filterQuery: "",
        readStatus: "all",
        sender: "",
        foldersInclude: [],
        foldersExclude: [],
        expirationDateTime: new Date(
          Date.now() + MAX_SUBSCRIPTION_MINUTES * 60 * 1000
        ).toISOString(),
      });
      return;
    }

    if (!data.value.folder) updateNodeValue(id, setNodes, { folder: "Inbox" });
    if (!data.value.changeType)
      updateNodeValue(id, setNodes, { changeType: "created,updated" });
    if (data.value.includeAttachments === undefined) {
      updateNodeValue(id, setNodes, { includeAttachments: false });
    }
    if (data.value.hasAttachmentsFilter === undefined) {
      updateNodeValue(id, setNodes, { hasAttachmentsFilter: false });
    }
    if (!data.value.readStatus)
      updateNodeValue(id, setNodes, { readStatus: "all" });
    if (!data.value.foldersInclude)
      updateNodeValue(id, setNodes, { foldersInclude: [] });
    if (!data.value.foldersExclude)
      updateNodeValue(id, setNodes, { foldersExclude: [] });
    if (data.value.filterQuery === undefined)
      updateNodeValue(id, setNodes, { filterQuery: "" });
    if (data.value.sender === undefined)
      updateNodeValue(id, setNodes, { sender: "" });
    if (!data.value.expirationDateTime) {
      updateNodeValue(id, setNodes, {
        expirationDateTime: new Date(
          Date.now() + MAX_SUBSCRIPTION_MINUTES * 60 * 1000
        ).toISOString(),
      });
    }

    if (!data.value.clientState) {
      updateNodeValue(id, setNodes, { clientState: generateClientState() });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    if (!data.output) return;
    updateNodeValue(id, setNodes, { lastReceivedAt: new Date().toISOString() });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.output]);

  const currentWebhookToken = projectId && id ? `${projectId}/${id}` : "";
  const isSubscriptionForThisNode = currentWebhookToken
    ? (data.value?.notificationUrl || "").includes(currentWebhookToken)
    : true;

  const isConnected =
    !!data.value?.subscriptionId &&
    !!data.value?.expirationDateTime &&
    new Date(data.value.expirationDateTime).getTime() > Date.now() &&
    !["disconnected", "disabled"].includes(String(data.value?.status || "").toLowerCase()) &&
    isSubscriptionForThisNode;
  const rawStatus = String(data.value?.status || "").toLowerCase();
  const resolvedStatus: "connected" | "renewing" | "error" | "disconnected" =
    rawStatus === "renewing"
      ? "renewing"
      : rawStatus === "error"
      ? "error"
      : isConnected
      ? "connected"
      : "disconnected";
  const statusLabel =
    resolvedStatus === "connected"
      ? "Connected"
      : resolvedStatus === "renewing"
      ? "Renewing"
      : resolvedStatus === "error"
      ? "Error"
      : "Disconnected";
  const inlineStatusText =
    statusText ||
    ((resolvedStatus === "error" && data.value?.lastError) || "");

  // const hasConnection = !!data.value?.connection;
  // const isWarning = !hasConnection || !isConnected;

  const handleChangeInput = (
    e: React.ChangeEvent<HTMLInputElement>,
    keyRef?: string
  ) => {
    if (keyRef) updateNodeValue(id, setNodes, { [keyRef]: e.target.value });
  };

  const handleAuthenticate = async (type: "add" | "remove", authData: any) => {
    if (type === "add") {
      const payload: Record<string, any> = { connection: authData };
      const inferredMailbox =
        authData?.mailboxEmail ||
        authData?.email ||
        authData?.mail ||
        authData?.userPrincipalName ||
        authData?.preferred_username;
      if (inferredMailbox) payload.mailboxEmail = inferredMailbox;
      console.log("Authenticated with Microsoft:", authData.access_token);
      tokenRef.current = authData.access_token || "";
      updateNodeValue(id, setNodes, payload);
    } else if (type === "remove") {
      tokenRef.current = "";
      updateNodeValue(id, setNodes, {
        connection: null,
        subscriptionId: "",
        expirationDateTime: "",
        status: "disconnected",
        lastError: "",
      });
    }
  };

  const buildAuthHeaders = (value: any) => {
    const bearer =
      tokenRef.current ||
      value?.connection?.access_token ||
      value?.connection?.accessToken ||
      "";
    return bearer ? { Authorization: `Bearer ${bearer}` } : undefined;
  };

  const isValidEmail = (value?: string) =>
    !value || /^\S+@\S+\.\S+$/.test(value.trim());

  const mergeConnectionState = (
    currentConnection: any,
    nextConnection: any
  ) => {
    if (!nextConnection) return currentConnection;
    return {
      ...(currentConnection || {}),
      ...nextConnection,
      id: nextConnection?.id || currentConnection?.id,
    };
  };

  const handleToggleFolder = (
    type: "include" | "exclude",
    folderId: string
  ) => {
    const key = type === "include" ? "foldersInclude" : "foldersExclude";
    const otherKey = type === "include" ? "foldersExclude" : "foldersInclude";
    const current: string[] = Array.isArray(latestValueRef.current?.[key])
      ? [...latestValueRef.current[key]]
      : [];
    const other: string[] = Array.isArray(latestValueRef.current?.[otherKey])
      ? [...latestValueRef.current[otherKey]]
      : [];

    if (current.includes(folderId)) {
      updateNodeValue(id, setNodes, {
        [key]: current.filter((id) => id !== folderId),
      });
      return;
    }

    const nextCurrent = [...current, folderId];
    const nextOther = other.filter((id) => id !== folderId);
    updateNodeValue(id, setNodes, {
      [key]: nextCurrent,
      [otherKey]: nextOther,
    });
  };

  const handleIncludeAllFolders = () => {
    if (!folders.length) return;
    const allIds = folders.map((f) => f.id);
    updateNodeValue(id, setNodes, {
      foldersInclude: allIds,
      foldersExclude: [],
    });
  };

  const allFoldersIncluded =
    folders.length > 0 &&
    (data.value?.foldersInclude?.length || 0) === folders.length &&
    (data.value?.foldersExclude?.length || 0) === 0;

  const maxExpirationDate = React.useCallback(
    () => new Date(Date.now() + MAX_SUBSCRIPTION_MINUTES * 60 * 1000),
    []
  );

  const handleEnable = async () => {
    if (!projectId) {
      setStatusText("Project ID not found in URL");
      return;
    }

    const value = latestValueRef.current || {};
    if (!value?.connection) {
      setStatusText("Please authenticate with Microsoft first.");
      return;
    }
    const mailboxEmail = (
      value.mailboxEmail ||
      value?.connection?.email ||
      value?.connection?.mailboxEmail ||
      value?.connection?.mail ||
      value?.connection?.userPrincipalName ||
      value?.connection?.preferred_username ||
      ""
    ).trim();
    if (!isValidEmail(value.sender)) {
      setStatusText("Sender email is not a valid email address.");
      return;
    }

    if (!value.clientState) {
      const newClientState = generateClientState();
      updateNodeValue(id, setNodes, { clientState: newClientState });
    }

    setIsLoading(true);
    setStatusText("Activating auto-managed subscription...");

    try {
      const headers = buildAuthHeaders(value);
      const cap = maxExpirationDate();
      let expirationDateTime = value.expirationDateTime;
      if (
        expirationDateTime &&
        new Date(expirationDateTime).getTime() > cap.getTime()
      ) {
        expirationDateTime = cap.toISOString();
      }

      const response = await axios.post(
        `${webURL}/triggers/outlook/subscription/enable`,
        {
          projectId,
          nodeId: id,
          mailboxEmail: mailboxEmail || undefined,
          folder: value.folder || "Inbox",
          changeType: value.changeType || "created,updated",
          includeAttachments: !!value.includeAttachments,
          tenantId: value.tenantId,
          clientId: value.clientId,
          clientSecret: value.clientSecret,
          clientState: value.clientState,
          subscriptionId: value.subscriptionId || undefined,
          connection: value.connection,
          expirationDateTime: expirationDateTime || undefined,
          filterQuery: value.filterQuery || "",
          hasAttachmentsFilter: !!value.hasAttachmentsFilter,
          readStatus: value.readStatus || "all",
          sender: value.sender || "",
          foldersInclude: value.foldersInclude || [],
          foldersExclude: value.foldersExclude || [],
        },
        headers ? { headers } : undefined
      );

      const payload = response.data || {};
      const mergedConnection = mergeConnectionState(value.connection, payload.connection);
      if (mergedConnection) {
        updateNodeValue(id, setNodes, { connection: mergedConnection });
      }

      updateNodeValue(id, setNodes, {
        mailboxEmail: payload.mailboxEmail ?? mailboxEmail ?? "",
        subscriptionId: payload.subscriptionId ?? value.subscriptionId ?? "",
        expirationDateTime:
          payload.expirationDateTime ?? value.expirationDateTime ?? "",
        status: payload.status ?? "connected",
        clientState: payload.clientState ?? value.clientState ?? "",
        notificationUrl: payload.notificationUrl ?? value.notificationUrl ?? "",
        filters: payload.filters ?? value.filters ?? {},
        lastError: payload.lastError ?? "",
        lastRenewAttemptAt:
          payload.lastRenewAttemptAt ?? value.lastRenewAttemptAt ?? "",
        lastRenewedAt: payload.lastRenewedAt ?? value.lastRenewedAt ?? "",
        renewRetryCount: payload.renewRetryCount ?? value.renewRetryCount ?? 0,
      });

      setStatusText("✅ Connected.");
    } catch (error: any) {
      const message = normalizeTriggerError(error);
      updateNodeValue(id, setNodes, { status: "error", lastError: message });
      setStatusText(message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDisable = async () => {
    if (!projectId) {
      setStatusText("Project ID not found in URL");
      return;
    }
    const value = latestValueRef.current || {};
    if (!value?.connection) {
      setStatusText("Please authenticate with Microsoft first.");
      return;
    }
    setIsLoading(true);
    setStatusText("Deactivating subscription...");

    try {
      const headers = buildAuthHeaders(value);
      await axios.post(
        `${webURL}/triggers/outlook/subscription/disable`,
        {
          projectId,
          nodeId: id,
          subscriptionId: value.subscriptionId || undefined,
        },
        headers ? { headers } : undefined
      );

      updateNodeValue(id, setNodes, {
        subscriptionId: "",
        expirationDateTime: "",
        status: "disconnected",
        lastError: "",
      });
      setStatusText("✅ Disconnected");
    } catch (error: any) {
      const message = normalizeTriggerError(error);
      updateNodeValue(id, setNodes, { status: "error", lastError: message });
      setStatusText(message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRetryNow = async () => {
    if (!projectId) {
      setStatusText("Project ID not found in URL");
      return;
    }
    const value = latestValueRef.current || {};
    if (!value?.connection) {
      setStatusText("Please authenticate with Microsoft first.");
      return;
    }
    const mailboxEmail = (
      value.mailboxEmail ||
      value?.connection?.email ||
      value?.connection?.mailboxEmail ||
      value?.connection?.mail ||
      value?.connection?.userPrincipalName ||
      value?.connection?.preferred_username ||
      ""
    ).trim();

    setIsLoading(true);
    setStatusText("Retrying subscription health...");
    try {
      const headers = buildAuthHeaders(value);
      const response = await axios.post(
        `${webURL}/triggers/outlook/subscription/retry`,
        {
          projectId,
          nodeId: id,
        },
        headers ? { headers } : undefined
      );
      const payload = response.data || {};
      const mergedConnection = mergeConnectionState(value.connection, payload.connection);
      if (mergedConnection) {
        updateNodeValue(id, setNodes, { connection: mergedConnection });
      }

      updateNodeValue(id, setNodes, {
        mailboxEmail: payload.mailboxEmail ?? mailboxEmail ?? "",
        subscriptionId: payload.subscriptionId ?? value.subscriptionId ?? "",
        expirationDateTime:
          payload.expirationDateTime ?? value.expirationDateTime ?? "",
        status: payload.status ?? "connected",
        clientState: payload.clientState ?? value.clientState ?? "",
        notificationUrl: payload.notificationUrl ?? value.notificationUrl ?? "",
        filters: payload.filters ?? value.filters ?? {},
        lastError: payload.lastError ?? "",
        lastRenewAttemptAt:
          payload.lastRenewAttemptAt ?? value.lastRenewAttemptAt ?? "",
        lastRenewedAt: payload.lastRenewedAt ?? value.lastRenewedAt ?? "",
        renewRetryCount: payload.renewRetryCount ?? value.renewRetryCount ?? 0,
      });

      setStatusText("✅ Retry completed");
    } catch (error: any) {
      const message = normalizeTriggerError(error);
      updateNodeValue(id, setNodes, { status: "error", lastError: message });
      setStatusText(message);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchFolders = async () => {
    const value = latestValueRef.current || {};
    const mailboxEmail = (
      value?.mailboxEmail ||
      value?.connection?.email ||
      value?.connection?.mailboxEmail ||
      value?.connection?.mail ||
      value?.connection?.userPrincipalName ||
      value?.connection?.preferred_username ||
      ""
    ).trim();
    setFolderError("");

    if (!value?.connection) {
      setFolderError("You can't fetch folders until you choose a connection.");
      return;
    }
    setIsFoldersLoading(true);
    try {
      const headers = buildAuthHeaders(value);
      const response = await axios.post(
        `${webURL}/triggers/outlook/folders`,
        {
          mailboxEmail: mailboxEmail || undefined,
          connection: value.connection,
          tenantId: value.tenantId,
          clientId: value.clientId,
          clientSecret: value.clientSecret,
        },
        headers ? { headers } : undefined
      );
      const payload = response.data || {};
      const folderList: OutlookFolder[] = payload.folders || [];
      setFolders(folderList);
      updateNodeValue(id, setNodes, {
        ...(payload.connection
          ? { connection: mergeConnectionState(value.connection, payload.connection) }
          : {}),
        mailboxEmail: payload.mailboxEmail ?? mailboxEmail ?? "",
      });
    } catch (err: any) {
      setFolderError(normalizeTriggerError(err));
      setFolders([]);
    } finally {
      setIsFoldersLoading(false);
    }
  };

  const isWarning = !data.value?.connection;

  return (
    <>
      <SettingsBlock nodeId={id} data={settings} setData={setSettings} />
      <Main>
        {isWarning ? (
          <>
            <div id="warning"></div>
            <span id="warningIcon"><IoWarningOutline size={21} /></span>
          </>
        ) : data.output ? (
          <>
            <div id="ran"></div>
            <span id="ranIcon">
              <IoCheckmarkCircleOutline size={21} />
            </span>
          </>
        ) : null}

        {isExpanded ? (
          <ExpandedBlock
            data={{ id, ...data }}
            handleClose={() => setIsExpanded(false)}
            isWarning={isWarning}
            >
            {isLoading ? (
              <Loader />
            ) : (
              <Styled>
                <AuthenticateSelect
                  type="microsoft"
                  defaultId={data.value?.connection?.id}
                  handleAuthenticate={handleAuthenticate}
                  background={data.background}
                />

                <div className="card">
                  <div className="cardHeader">
                    <h4>Connection</h4>
                    <span className={`badge ${resolvedStatus}`}>
                      {statusLabel}
                    </span>
                  </div>
                  <p className="muted connectionMeta">
                    Select a Microsoft connection and manage your trigger state.
                  </p>
                </div>

                <div className="card">
                  <div className="buttons">
                    <button
                      className="btn1 withBorder"
                      onClick={handleEnable}
                      disabled={isLoading || !data.value?.connection}>
                      <FontAwesomeIcon icon={faSync} spin={isLoading} />{" "}
                      Activate
                    </button>
                    <button
                      className="btn1 withBorder"
                      onClick={handleRetryNow}
                      disabled={isLoading || !data.value?.connection}>
                      <FontAwesomeIcon icon={faSync} spin={isLoading} /> Retry
                      now
                    </button>
                    <button
                      className="btn1 withBorder"
                      onClick={handleDisable}
                      disabled={isLoading || !data.value?.connection}>
                      <FontAwesomeIcon icon={faSync} spin={isLoading} />{" "}
                      Deactivate
                    </button>
                  </div>
                  {inlineStatusText ? (
                    <div
                      className={`statusText ${
                        inlineStatusText.includes("✅")
                          ? "success"
                          : inlineStatusText.toLowerCase().includes("error")
                          ? "error"
                          : ""
                      }`}>
                      {inlineStatusText}
                    </div>
                  ) : null}
                </div>

                <div className="section">
                  <Switch
                    title="Include Attachments"
                    value={!!data.value?.includeAttachments}
                    setValue={() =>
                      updateNodeValue(id, setNodes, {
                        includeAttachments: !data.value?.includeAttachments,
                      })
                    }
                  />
                </div>

                <div className="card">
                  <div className="section">
                    <div className="cardHeader">
                      <h4>Filters</h4>
                      <button
                        type="button"
                        className="btn1 withBorder"
                        onClick={() => setShowFilters((prev) => !prev)}>
                        {showFilters ? "Hide filters" : "Show filters"}
                      </button>
                    </div>
                    {!showFilters ? (
                      <p className="muted filterSummary">{filterSummary}</p>
                    ) : null}

                    {showFilters ? (
                      <div className="scroll">
                        <TextInput
                          keyRef="filterQuery"
                          value={data.value?.filterQuery || ""}
                          title="Custom filter"
                          placeholder={`e.g. "isRead eq false"`}
                          handleChange={handleChangeInput}
                        />
                        <p className="muted">
                          Supports Graph message filters (e.g.,
                          categories/any(), importance eq 'high', isRead eq
                          false).
                        </p>

                        {/* <div className="rowSelect">
                          <Select
                            title="Read Status"
                            data={readStatusOptions}
                            handleSelectItem={(selectedId) => {
                              const selected = readStatusOptions.find(
                                (opt) => opt.id === selectedId
                              );
                              updateNodeValue(id, setNodes, {
                                readStatus: selected?.refId || "all",
                              });
                            }}
                            isSmall={true}
                            emptyMsg="Choose status"
                          />
                        </div>
                        <p className="muted">
                          Restrict to unread or already-read messages.
                        </p> */}

                        <TextInput
                          keyRef="sender"
                          value={data.value?.sender || ""}
                          title="Sender email"
                          placeholder="sender@example.com"
                          handleChange={handleChangeInput}
                        />
                        <p className="muted">
                          Optional; triggers only when the From address matches.
                        </p>
                        {/* <DateInput
                          keyRef="expirationDateTime"
                          value={formatDateForInput(data.value?.expirationDateTime || defaultExpirationIso)}
                          title="Subscription expiration (UTC)"
                          isWithTime
                          handleChange={handleChangeDateInput}
                        /> */}
                        {/* <p className="muted">
                          Choose when this subscription should expire/renew. Microsoft may cap the max duration.
                        </p> */}
                        <div
                          className="folderList"
                          >
                          <div className="cardHeader folderHeader">
                            <h4>Folders</h4>
                            <button
                              type="button"
                              className="btn1 withBorder"
                              onClick={fetchFolders}
                              disabled={isFoldersLoading || !data.value?.connection}>
                              <FontAwesomeIcon
                                icon={faSync}
                                spin={isFoldersLoading}
                              />{" "}
                              Fetch folders
                            </button>
                            <button
                              type="button"
                              className={`btn1 withBorder ${
                                allFoldersIncluded ? "active" : ""
                              }`}
                              onClick={handleIncludeAllFolders}
                              disabled={!folders.length}>
                              Include all
                            </button>
                          </div>

                          {!data.value?.connection ? (
                            <div
                              className="statusText error"
                              style={{ marginBottom: "8px" }}>
                              You can't fetch folders until you choose a connection.
                            </div>
                          ) : null}

                          {folderError && data.value?.connection ? (
                            <div
                              className="statusText error"
                              style={{ marginBottom: "8px" }}>
                              {folderError}
                            </div>
                          ) : null}

                          {isFoldersLoading || folders.length > 0 ? (
                            <>
                              {isFoldersLoading && folders.length === 0 ? (
                                <div className="muted">Loading folders…</div>
                              ) : null}

                              {!isFoldersLoading && !folders.length ? (
                                <div className="muted">
                                  No folders loaded yet. Fetch to browse and
                                  add.
                                </div>
                              ) : null}

                              {folders.length > 0 ? (
                                <div className="folderRows">
                                  {folders.map((folder) => (
                                    <div key={folder.id} className="folderRow">
                                      <div className="folderInfo">
                                        <div className="folderName">
                                          {folder.displayName || "(no name)"}
                                        </div>
                                        <div className="muted folderMeta">
                                          {folder.totalItemCount !== undefined
                                            ? `total ${
                                                folder.totalItemCount
                                              }, unread ${
                                                folder.unreadItemCount ?? 0
                                              }`
                                            : ""}
                                        </div>
                                      </div>
                                      <div className="folderActions">
                                        {(() => {
                                          const isIncluded = (
                                            data.value?.foldersInclude || []
                                          ).includes(folder.id);
                                          const isExcluded = (
                                            data.value?.foldersExclude || []
                                          ).includes(folder.id);
                                          return (
                                            <>
                                              <button
                                                type="button"
                                                className={`btn1 withBorder ${
                                                  isIncluded ? "active" : ""
                                                }`}
                                                onClick={() =>
                                                  handleToggleFolder(
                                                    "include",
                                                    folder.id
                                                  )
                                                }>
                                                {isIncluded
                                                  ? "Included"
                                                  : "Include"}
                                              </button>
                                              <button
                                                type="button"
                                                className={`btn1 withBorder ${
                                                  isExcluded ? "active" : ""
                                                }`}
                                                onClick={() =>
                                                  handleToggleFolder(
                                                    "exclude",
                                                    folder.id
                                                  )
                                                }>
                                                {isExcluded
                                                  ? "Excluded"
                                                  : "Exclude"}
                                              </button>
                                            </>
                                          );
                                        })()}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              ) : null}
                            </>
                          ) : null}
                        </div>
                      </div>
                    ) : null}
                  </div>
                </div>
              </Styled>
            )}
          </ExpandedBlock>
        ) : null}
      </Main>
    </>
  );
}
